import { pickT, sanitizeReturnTo } from "@repo/shared";
import type {
  LocaleCode,
  PublicLinkContactInfo,
  PublicLinkUnavailableReason,
  Translated,
} from "@repo/shared/types";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { CodeEntryForm } from "@/components/public-link-auth/code-entry-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, post } from "@/lib/api/_client";
import {
  getPublicLinkContactInfo,
  languageFromError,
  unavailableReasonFromError,
} from "@/lib/api/public-flow/contact-info";
import { useSession } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n/context";
import { T } from "./page.i18n";

/** Maps `PublicLinkUnavailableReason` to the localized message. Unknown/absent falls back to `linkNotFound`. */
function unavailableReasonText(
  t: Translated<typeof T>,
  reason: PublicLinkUnavailableReason | null,
): string {
  switch (reason) {
    case "expired":
      return t.linkExpired;
    case "revoked":
      return t.linkRevoked;
    case "representative_removed":
      return t.representativeRemoved;
    case "representative_changed":
      return t.representativeChanged;
    case "member_removed":
      return t.memberRemoved;
    default:
      return t.linkNotFound;
  }
}

/**
 * QL-570 — the API picks the Idura environment (test or production) from the
 * tenant's mode and redirects to its authorize URL, so the login and the
 * callback always use the same one. The eID always proves the signer's
 * national id (QL-505): see `criipto-auth-service.ts`.
 */
function buildCriiptoAuthUrl(publicToken: string, returnTo: string | null) {
  const startUrl = new URL(`${process.env.BUN_PUBLIC_API_URL}/public/criipto/auth/start`);
  startUrl.searchParams.set("publicToken", publicToken);
  if (returnTo) startUrl.searchParams.set("returnTo", returnTo);
  return startUrl.toString();
}

export default function PublicAuthPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  // QL-17 issue 027 — the flow's language, from the contact-info response or
  // a refused link's error body. Until it is known (and for an advisor, who
  // never loads it) the viewer's own language.
  const { userLanguage } = useUserLanguage();
  const [flowLanguage, setFlowLanguage] = useState<LocaleCode | null>(null);
  const t = pickT(T, flowLanguage ?? userLanguage);
  const publicToken = params.token as string;
  const returnTo = searchParams.get("returnTo");
  const errorCode = searchParams.get("error");

  const [contactInfo, setContactInfo] = useState<PublicLinkContactInfo | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<PublicLinkUnavailableReason | null>(
    null,
  );
  const [loadingContactInfo, setLoadingContactInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [lastSentMethod, setLastSentMethod] = useState<"email" | "sms" | null>(null);
  const [isPending, startTransition] = useTransition();
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Better Auth advisor session, shared app-wide via the session atom
  const { data: advisorSession, isPending: advisorSessionPending } = useSession();
  const isAdvisor = !!advisorSession?.user;

  // Check if advisor is logged in and show error
  useEffect(() => {
    // Wait until the advisor session check has settled
    if (advisorSessionPending) return;

    if (isAdvisor) {
      // Advisor is logged in - show error
      setError(t.advisorCannotAccess);
      setLoadingContactInfo(false);
      return;
    }

    // Not logged in as advisor - load contact info
    getPublicLinkContactInfo(publicToken)
      .then((result) => {
        if (result.success && result.data) {
          setFlowLanguage(result.data.language);
          setContactInfo(result.data);
        }
        setLoadingContactInfo(false);
      })
      .catch((err) => {
        setFlowLanguage(languageFromError(err));
        setUnavailableReason(unavailableReasonFromError(err));
        setLoadingContactInfo(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicToken, advisorSessionPending, isAdvisor]);

  // Clear any existing public session for a different link
  useEffect(() => {
    const storedFor = localStorage.getItem("publicSessionToken_for");
    if (storedFor && storedFor !== publicToken) {
      localStorage.removeItem("publicSessionToken");
      localStorage.removeItem("publicSessionToken_for");
    }
  }, [publicToken]);

  const handleCriiptoLogin = () => {
    window.location.href = buildCriiptoAuthUrl(publicToken, returnTo);
  };

  const sendCode = async (channel: "email" | "sms") => {
    setLastSentMethod(channel);
    setError(null);
    setSuccess(null);
    setIsSendingCode(true);

    try {
      const result = await post<any>(`/public/flow/${publicToken}/send-code`, { channel });

      if (!result.success) {
        setError(result.error || t.failedToSendCode);
        return;
      }

      setSuccess(channel === "sms" ? t.codeSentSMS : t.codeSentEmail);
      setCodeSent(true);
    } catch (_err) {
      setError(t.failedToSendCode);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!lastSentMethod) return;
    setError(null);
    setSuccess(null);
    setIsSendingCode(true);
    try {
      const result = await post<any>(`/public/flow/${publicToken}/send-code`, {
        channel: lastSentMethod,
      });
      if (!result.success) {
        setError(result.error || t.failedToSendCode);
        return;
      }
      setSuccess(lastSentMethod === "sms" ? t.codeSentSMS : t.codeSentEmail);
    } catch (_err) {
      setError(t.failedToSendCode);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerify = (code: string) => {
    setError(null);
    startTransition(async () => {
      try {
        const result = await post<any>(`/public/flow/${publicToken}/verify-code`, { code });

        // Store session token in localStorage
        if (result.data?.sessionToken) {
          localStorage.setItem("publicSessionToken", result.data.sessionToken);
          localStorage.setItem("publicSessionToken_for", publicToken);

          // Origin-lock returnTo so a forged value cannot navigate off-origin; fall
          // back to the flow view when it is rejected.
          const redirectUrl =
            sanitizeReturnTo(returnTo, window.location.origin) ?? `/p/${publicToken}`;
          navigate(redirectUrl);
        }
      } catch (err) {
        // A wrong code returns HTTP 401, so `post` throws. Surface it inline and keep
        // the client on the code-entry screen (codeSent stays true) so they can retry.
        const message = err instanceof ApiError ? err.message : null;
        // The server already translates the error (lockout included) into the flow language.
        setError(message || t.authFailed);
      }
    });
  };

  // Show loading state while fetching contact info
  if (loadingContactInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error if advisor is logged in
  if (error === t.advisorCannotAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // The link no longer works (expired/revoked/representative removed/not found) —
  // a proper message per reason, never the old generic "contact support" fallback.
  if (unavailableReason || !contactInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{unavailableReasonText(t, unavailableReason)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { hasEmail, hasPhone, emailHint, phoneHint, bankIdAvailable, representativeName } =
    contactInfo;

  // A group whose members answer for themselves has no single signer on the
  // group link: the server refuses sign-in there and points members to their
  // own links.
  if (errorCode === "use_member_links" || contactInfo.useMemberLinks) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t.useMemberLinks}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // The BankID callback rejected a signer whose national id didn't match the
  // expected representative — offer to retry rather than the generic auth-error page.
  if (errorCode === "identity_mismatch") {
    const message = representativeName
      ? t.identityMismatch(representativeName)
      : t.identityMismatchGeneric;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{message}</AlertDescription>
          </Alert>
          {bankIdAvailable && <Button onClick={handleCriiptoLogin}>{t.tryAgain}</Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">
            {representativeName
              ? t.signInAsRepresentative(representativeName)
              : t.verifyYourIdentity}
          </CardTitle>
          <CardDescription>
            {codeSent ? t.enterCodeToContinue : t.sendVerificationCode}
          </CardDescription>
          {representativeName && !codeSent && (
            <p className="text-xs text-muted-foreground pt-1">
              {t.representativeNote(representativeName)}
            </p>
          )}
        </CardHeader>

        <CardContent>
          {!codeSent ? (
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="text-center space-y-2 py-2">
                <p className="text-sm text-muted-foreground">{t.chooseMethod}</p>
              </div>

              <div className="space-y-2">
                {hasEmail && (
                  <Button
                    type="button"
                    onClick={() => sendCode("email")}
                    disabled={isSendingCode}
                    className="w-full gap-2"
                    variant={hasPhone ? "outline" : "default"}
                    loading={isSendingCode && lastSentMethod === "email"}
                  >
                    {t.sendCodeViaEmail}
                    {emailHint ? ` (${emailHint})` : ""}
                  </Button>
                )}

                {hasPhone && (
                  <Button
                    type="button"
                    onClick={() => sendCode("sms")}
                    disabled={isSendingCode}
                    className="w-full gap-2"
                    variant={hasEmail ? "outline" : "default"}
                    loading={isSendingCode && lastSentMethod === "sms"}
                  >
                    {t.sendCodeViaSMS}
                    {phoneHint ? ` (${phoneHint})` : ""}
                  </Button>
                )}
              </div>

              {bankIdAvailable && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        {t.orSignInWith}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCriiptoLogin}
                    className="w-full gap-2"
                  >
                    {t.bankIdMitId}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">{t.secureAuth}</p>
                </>
              )}
            </div>
          ) : (
            <CodeEntryForm
              channel={lastSentMethod ?? "email"}
              onVerify={handleVerify}
              onResend={handleResendCode}
              verifying={isPending}
              resending={isSendingCode}
              error={error}
              success={success}
              language={flowLanguage ?? userLanguage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
